import {
  Link,
  useCanGoBack,
  useNavigate,
  useParams,
  useRouter,
} from '@tanstack/react-router';
import { format } from 'date-fns';
import { CircleArrowLeft, Loader2 } from 'lucide-react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { useGetEvent, useUpdateEventMutation } from '~entities/events';
import { useGetSessionById } from '~entities/session/api/get-session-by-id';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import {
  calculateDurationInMinutes,
  formatDateTime,
  isValidTimeRange,
  normalizeDate,
  normalizeTime,
} from '~features/classes/ui/helpers';
import {
  buildEventFormAutoHideHint,
  isEventAutoHideGraceHours,
} from '~features/events/events.helpers';
import {
  DEFAULT_EVENT_AUTO_HIDE_GRACE_HOURS,
  EVENT_TIME_CONFIG,
  EventAutoHideGraceHours,
} from '~features/events/events.types';
import { EventPortalVisibilityFields } from '~features/events/ui/event-portal-visibility-fields';
import { EventPublicShareLinkSection } from '~features/events/ui/event-public-share-link-section';
import { CoverImageSelector } from '~features/shared/cover-image-selector';
import { LocationSelector } from '~features/shared/location-selector';
import { PolicySelector } from '~features/shared/policy-selector';
import { SessionSelector } from '~features/shared/session-selector';
import { TimePickerDropdown } from '~features/shared/time-picker-dropdown';

import { DateTimePicker } from '~shared/components/date-time-picker';
import { MAX_AGE_YEARS } from '~shared/constants/age';
import { colorOptions } from '~shared/constants/colors';
import { TuitionBillingCycle } from '~shared/constants/tuition-billing-cycle';
import { extractDateOnly } from '~shared/helpers/datetime';
import { useUnsavedChangesBlocker } from '~shared/hooks/use-unsaved-changes-blocker';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import { Switch } from '~shared/shadcn/ui/switch';

import { GroupsSelector } from '../../shared/groups-selector';
import { InstructorSelector } from '../../shared/instructor-selector';
import { RoomSelector } from '../../shared/room-selector';
import { TagsSelector } from '../../shared/tags-selector';
import { EventDateSelectionSwitch } from './event-date-selection-switch';
import { mapEventToFormData } from './map-event-to-form-data';
import type { FormData } from './update-event-page.types';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

const getEventUpdateErrorMessage = (error: unknown) => {
  const fallback = 'Failed to update event. Please check all required fields.';
  const responseData = (
    error as
      | {
          response?: {
            data?:
              | string
              | {
                  message?: string | Array<string>;
                  error?: string;
                };
          };
        }
      | null
      | undefined
  )?.response?.data;

  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData;
  }

  const responseMessage =
    typeof responseData === 'object' ? responseData.message : undefined;

  if (Array.isArray(responseMessage)) {
    return responseMessage.join(', ');
  }

  if (typeof responseMessage === 'string' && responseMessage.trim()) {
    return responseMessage;
  }

  const responseError =
    typeof responseData === 'object' ? responseData.error : undefined;

  if (typeof responseError === 'string' && responseError.trim()) {
    return responseError;
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

export const UpdateEventPage: React.FC = () => {
  const { eventId } = useParams({
    from: '/_auth/_layout/events/$eventId/update',
  });
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const navigate = useNavigate({ from: '/events/$eventId/update' });
  const mutation = useUpdateEventMutation();
  const { data: studioDetails } = useGetStudioDetails();
  const studioProrationEnabled = studioDetails?.prorationEnabled !== false;
  const {
    data: eventData,
    isPending,
    isFetching,
    isError,
  } = useGetEvent({ eventId });

  const [selectedFile, setSelectedFile] = useState<File | string | null>(null);
  const [selectedFileType, setSelectedFileType] = useState<'file' | 'url'>(
    'file',
  );
  const [isFileChanged, setIsFileChanged] = useState(false);

  const handleFileSelect = (
    file: File | string | null,
    fileType: 'file' | 'url',
  ) => {
    setSelectedFile(file);
    setSelectedFileType(fileType);
    setIsFileChanged(true);
  };

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    trigger,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    mode: 'onChange',
  });

  const { dialog: unsavedChangesDialog, allowNextNavigation } =
    useUnsavedChangesBlocker(isDirty);

  useEffect(() => {
    if (eventData) {
      reset(mapEventToFormData(eventData));
    }
  }, [eventData?._id, reset]);

  const startTime = watch('times.startTime');
  const endTime = watch('times.endTime');
  const selectedDays = watch('days') ?? [];

  useEffect(() => {
    if (startTime && endTime) {
      setValue(
        'times.duration',
        calculateDurationInMinutes(startTime, endTime),
      );
    }
  }, [startTime, endTime, setValue]);

  useEffect(() => {
    // Register days field with validation
    register('days', {
      validate: (value) => value.length > 0 || 'Please select at least one day',
    });
  }, [register]);

  const sessionId = watch('eventInfo.session.value');

  // Fetch session data
  const { data: sessionData } = useGetSessionById(sessionId, {
    enabled: !!sessionId,
  });

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      // Validate days selection
      if (data.days.length === 0) {
        // Trigger validation to show the error message
        setValue('days', [], { shouldValidate: true });
        toast.error('Please select at least one day');
        return;
      }

      console.log('Form data:', data); // Debug log

      const startDate = normalizeDate(data.dates.startDate);
      const endDate = normalizeDate(data.dates.endDate);

      const startTime = normalizeTime(data.times.startTime);
      const endTime = normalizeTime(data.times.endTime);

      // Hard guard: never allow saving non-positive time ranges.
      if (
        data.timeConfig === 'same' &&
        !isValidTimeRange(startTime, endTime)
      ) {
        setError('times.endTime', {
          type: 'manual',
          message: 'End time must be after start time',
        });
        toast.error('End time must be after start time');
        return;
      }

      if (data.timeConfig === 'different') {
        for (const day of data.days) {
          const dayStart = normalizeTime(data.dayTimes[day]?.startTime ?? '');
          const dayEnd = normalizeTime(data.dayTimes[day]?.endTime ?? '');
          if (dayStart && dayEnd && !isValidTimeRange(dayStart, dayEnd)) {
            setError(`dayTimes.${day}.endTime`, {
              type: 'manual',
              message: 'End time must be after start time',
            });
            toast.error(`End time must be after start time for ${day}`);
            return;
          }
        }
      }

      // Format final date-time strings
      const formattedStartDate = formatDateTime(startDate, startTime);
      const formattedEndDate = formatDateTime(startDate, endTime);

      const availability =
        data.timeConfig === 'same'
          ? data.days.map((day) => ({
              day,
              startTime: formattedStartDate,
              endTime: formattedEndDate,
            }))
          : data.days.map((day) => {
              const dayST = normalizeTime(
                data.dayTimes[day]?.startTime ?? startTime,
              );
              const dayET = normalizeTime(
                data.dayTimes[day]?.endTime ?? endTime,
              );
              return {
                day,
                startTime: formatDateTime(startDate, dayST),
                endTime: formatDateTime(startDate, dayET),
              };
            });

      // Ensure required fields have values
      if (!data.eventInfo.instructor) {
        data.eventInfo.instructor = [];
      }
      if (!data.otherInfo.tags) {
        data.otherInfo.tags = [];
      }
      if (!data.otherInfo.group) {
        data.otherInfo.group = [];
      }
      if (!data.otherInfo.policyGroup) {
        data.otherInfo.policyGroup = [];
      }

      const payload = {
        eventId,
        file: isFileChanged ? selectedFile : null,
        fileType: selectedFileType,
        eventDetails: {
          title: data.eventInfo.eventName,
          hide: data.eventInfo.hide,
          autoHideAfterEnd: data.eventInfo.autoHideAfterEnd,
          autoHideGraceHours: data.eventInfo.autoHideGraceHours,
          includeInPublicSchedule: data.eventInfo.includeInPublicSchedule,
          allowSpecificDatesSelection:
            data.eventInfo.allowSpecificDatesSelection,
          color: data.eventInfo.color,
          location: data.eventInfo.location.value ?? undefined,
          room: data.eventInfo.room.value ?? undefined,
          instructor: data.eventInfo.instructor.map(
            (instructor) => instructor.value,
          ),
          maxSize: Number(data.eventInfo.seats),
          startYear: data.eventInfo.minAge.year,
          startMonth: data.eventInfo.minAge.month,
          endYear: data.eventInfo.maxAge.year,
          endMonth: data.eventInfo.maxAge.month,
          group: data.otherInfo.group.map((group) => group.value),
          tags: data.otherInfo.tags.map((tag) => tag.value),
          startDate: startDate,
          endDate: endDate,
          startTime: startTime,
          endTime: endTime,
          duration: Number(data.times.duration),
          registrationStartDate: data.dates.registrationStartDate,
          registrationFeeAmount: Number(data.otherInfo.registrationFee) || 0,
          costumeFee: Number(data.otherInfo.costumeFee) || 0,
          description: data.otherInfo.description || '',
          policyGroup: data.otherInfo.policyGroup.map((policy) => policy.value),
          days: data.days,
          oneTime:
            data.otherInfo.tuitionBillingCycle === TuitionBillingCycle.ONE_TIME,
          tuitionBillingCycle: data.otherInfo.tuitionBillingCycle || '',
          availability,
          timeConfig:
            data.otherInfo.tuitionBillingCycle === TuitionBillingCycle.ONE_TIME
              ? 'same'
              : data.timeConfig,
          session: data.eventInfo.session.value,
          tuitionFee: Number(data.otherInfo.tuitionFee) || 0,
          prorateTuition: data.otherInfo.prorateTuition,
          ...(data.otherInfo.tuitionBillingCycle === 'monthly' && {
            billingDay: data.otherInfo.billingDay,
          }),
          ...(data.otherInfo.tuitionBillingCycle === TuitionBillingCycle.EVERY_X_DAYS && {
            billingFrequencyDays: data.otherInfo.billingFrequencyDays,
          }),
        },
      };

      console.log('Mutation payload:', payload); // Debug log

      await mutation.mutateAsync(payload);
      // Clear dirty state + bypass blocker before navigate (isDirty lags one render).
      reset(data);
      allowNextNavigation();
      toast.success('Event updated successfully!');
      void navigate({ to: '/events' });
    } catch (error) {
      console.error('Failed to update event:', error);
      toast.error(getEventUpdateErrorMessage(error));
    }
  };

  if (isPending || (isFetching && !eventData)) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <Loader2 className='size-8 animate-spin' />
      </div>
    );
  }

  if (isError || !eventData) {
    return (
      <div className='flex h-screen flex-col items-center justify-center gap-4'>
        <p className='text-muted-foreground'>
          Unable to load event details. Please try again.
        </p>
        <Button variant='outline' asChild>
          <Link to='/events'>Back to Events</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className='pb-56'>
      {canGoBack ? (
        <Button
          variant={'ghost'}
          className='flex items-center p-2 [&_svg]:size-6'
          disabled={!canGoBack}
          onClick={() => {
            router.history.back();
          }}
        >
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>Edit Event</h2>
        </Button>
      ) : (
        <Link to='/events' className='flex items-center gap-2'>
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>Edit Event</h2>
        </Link>
      )}

      <Card className='mx-auto mt-6 w-full max-w-4xl border border-border shadow-none'>
        <CoverImageSelector
          key={eventData._id}
          onFileSelect={handleFileSelect}
          imageUrl={
            eventData.webpImageUrl ||
            eventData.originalImageUrl ||
            eventData.imageUrl ||
            eventData.defaultImageUrl
          }
        />
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
            {/* Event Info Section */}
            <div className='space-y-4'>
              <h2 className='text-xl font-semibold'>Event Info</h2>
              <div className='space-y-2'>
                <Label>Event Name</Label>
                <Input
                  {...register('eventInfo.eventName', {
                    required: 'Event name is required',
                  })}
                  placeholder='Event Name'
                />
                {errors.eventInfo?.eventName && (
                  <span className='text-sm text-destructive'>
                    {errors.eventInfo.eventName.message}
                  </span>
                )}
              </div>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-4'>
                  <div className='flex items-center space-x-2'>
                    <Label htmlFor='color'>Color</Label>
                    <Select
                      value={watch('eventInfo.color')}
                      onValueChange={(value) => {
                        setValue('eventInfo.color', value);
                      }}
                    >
                      <SelectTrigger className='w-[180px]'>
                        <SelectValue placeholder='Select a color'>
                          <div className='flex items-center space-x-2'>
                            {watch('eventInfo.color') && (
                              <div
                                className='size-4 rounded-full'
                                style={{
                                  backgroundColor: watch('eventInfo.color'),
                                }}
                              />
                            )}
                            <span>
                              {colorOptions.find(
                                (c) => c.value === watch('eventInfo.color'),
                              )?.label ?? 'Select a color'}
                            </span>
                          </div>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {colorOptions.map((color) => (
                          <SelectItem key={color.value} value={color.value}>
                            <div className='flex items-center space-x-2'>
                              <div
                                className='size-4 rounded-full'
                                style={{ backgroundColor: color.value }}
                              />
                              <span>{color.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className='space-y-2'>
                <EventPortalVisibilityFields
                  hide={watch('eventInfo.hide')}
                  onHideChange={(checked) => {
                    setValue('eventInfo.hide', checked);
                  }}
                  hideLabel='Hide from parent portal - not shown in family browse or search'
                  autoHideAfterEnd={watch('eventInfo.autoHideAfterEnd') !== false}
                  onAutoHideChange={(checked) => {
                    setValue('eventInfo.autoHideAfterEnd', checked);
                  }}
                  autoHideGraceHours={
                    isEventAutoHideGraceHours(
                      watch('eventInfo.autoHideGraceHours'),
                    )
                      ? (watch(
                          'eventInfo.autoHideGraceHours',
                        ) as EventAutoHideGraceHours)
                      : DEFAULT_EVENT_AUTO_HIDE_GRACE_HOURS
                  }
                  onGraceChange={(hours) => {
                    setValue('eventInfo.autoHideGraceHours', hours);
                  }}
                  showGrace={
                    watch('eventInfo.autoHideAfterEnd') !== false &&
                    Boolean(watch('dates.endDate'))
                  }
                  visibilityHint={buildEventFormAutoHideHint({
                    autoHideAfterEnd:
                      watch('eventInfo.autoHideAfterEnd') !== false,
                    autoHideGraceHours: isEventAutoHideGraceHours(
                      watch('eventInfo.autoHideGraceHours'),
                    )
                      ? watch('eventInfo.autoHideGraceHours')
                      : DEFAULT_EVENT_AUTO_HIDE_GRACE_HOURS,
                    endDate: watch('dates.endDate'),
                    endTime: watch('times.endTime'),
                    timeConfig: watch('timeConfig'),
                    availability:
                      watch('timeConfig') === EVENT_TIME_CONFIG.DIFFERENT
                        ? (watch('days') ?? []).map((day) => ({
                            day,
                            endTime: watch('dayTimes')?.[day]?.endTime,
                          }))
                        : [],
                  })}
                />
              </div>

              <div className='flex items-start gap-2'>
                <Switch
                  id='upd-includeInPublicSchedule'
                  className='mt-0.5 shrink-0'
                  checked={watch('eventInfo.includeInPublicSchedule')}
                  onCheckedChange={(v) => {
                    setValue('eventInfo.includeInPublicSchedule', v);
                  }}
                />
                <Label
                  htmlFor='upd-includeInPublicSchedule'
                  className='min-w-0 cursor-pointer font-normal'
                >
                  Include this event to the &nbsp;
                  <Link
                    to='/public-page-link'
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                    }}
                    className='font-medium text-primary underline underline-offset-4 hover:no-underline'
                  >
                    Public Events Directory
                  </Link>
                </Label>
              </div>

              <EventPublicShareLinkSection
                eventId={eventId}
                eventData={eventData}
                currentStudio={
                  studioDetails
                    ? {
                        _id: studioDetails._id,
                        locationId: studioDetails.locationId,
                      }
                    : undefined
                }
              />
              <div className='space-y-2'>
                <Label>Location</Label>
                <Controller
                  control={control}
                  name='eventInfo.location'
                  render={({ field }) => (
                    <LocationSelector
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                {errors.eventInfo?.location && (
                  <span className='text-sm text-destructive'>
                    {errors.eventInfo.location.message}
                  </span>
                )}
              </div>
              <div className='space-y-2'>
                <Label>Room</Label>
                <Controller
                  control={control}
                  name='eventInfo.room'
                  render={({ field }) => (
                    <RoomSelector
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                {errors.eventInfo?.room && (
                  <span className='text-sm text-destructive'>
                    {errors.eventInfo.room.message}
                  </span>
                )}
              </div>
              <div className='space-y-2'>
                <Label>Session</Label>
                <Controller
                  control={control}
                  name='eventInfo.session'
                  rules={{
                    required: 'Session is required',
                    validate: (session, formValues) => {
                      if ((session && !session.value) || session === undefined)
                        return 'Session selection is required';

                      if (!formValues.dates.startDate) return true;

                      if (!sessionData) return true;

                      if (!('billingDate' in sessionData)) return true;

                      if (!sessionData.billingDate) return true;

                      const billingMoment = moment(
                        sessionData.billingDate,
                      ).startOf('day');
                      const classMoment = moment(
                        formValues.dates.startDate,
                      ).startOf('day');

                      const formattedBillingDate =
                        billingMoment.format('MMM D, YYYY');

                      return (
                        billingMoment.isSameOrBefore(classMoment) ||
                        `Session billing date (${formattedBillingDate}) cannot be after event start date`
                      );
                    },
                  }}
                  render={({ field }) => (
                    <SessionSelector
                      session={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                {typeof errors.eventInfo?.session?.message === 'string' && (
                  <span className='text-sm text-destructive'>
                    {errors.eventInfo.session.message}
                  </span>
                )}
              </div>
              <div className='space-y-2'>
                <Label>Number of Seats</Label>
                <Input
                  type='number'
                  {...register('eventInfo.seats', {
                    required: 'Number of seats is required',
                    min: { value: 1, message: 'Must have at least 1 seat' },
                    valueAsNumber: true,
                  })}
                  placeholder='Number of Seats'
                />
                {errors.eventInfo?.seats && (
                  <span className='text-sm text-destructive'>
                    {errors.eventInfo.seats.message}
                  </span>
                )}
              </div>
              <div className='flex gap-4'>
                <div className='flex-1 space-y-2'>
                  <Label>Min Age</Label>
                  <div className='flex gap-4'>
                    <div>
                      <Input
                        type='number'
                        {...register('eventInfo.minAge.year', {
                          required: 'Enter the minimum age year.',
                          min: {
                            value: 0,
                            message: 'Minimum age year must be at least 0.',
                          },
                          max: {
                            value: MAX_AGE_YEARS,
                            message: `Minimum age year must be at most ${MAX_AGE_YEARS}.`,
                          },
                          valueAsNumber: true,
                        })}
                        placeholder='year'
                      />
                      {typeof errors.eventInfo?.minAge?.year?.message ===
                        'string' && (
                        <span className='text-sm text-destructive'>
                          {errors.eventInfo.minAge.year.message}
                        </span>
                      )}
                    </div>
                    <div>
                      <Input
                        type='number'
                        {...register('eventInfo.minAge.month', {
                          required: 'Enter the minimum age month.',
                          min: {
                            value: 0,
                            message: 'Minimum age month must be at least 0.',
                          },
                          max: {
                            value: 11,
                            message: 'Minimum age month must be at most 11.',
                          },
                          valueAsNumber: true,
                        })}
                        placeholder='month'
                      />
                      {typeof errors.eventInfo?.minAge?.month?.message ===
                        'string' && (
                        <span className='text-sm text-destructive'>
                          {errors.eventInfo.minAge.month.message}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className='flex-1 space-y-2'>
                  <Label>Max Age</Label>
                  <div className='flex gap-4'>
                    <div>
                      <Input
                        type='number'
                        {...register('eventInfo.maxAge.year', {
                          required: 'Enter the maximum age year.',
                          min: {
                            value: 1,
                            message: 'Maximum age year must be at least 1.',
                          },
                          max: {
                            value: MAX_AGE_YEARS,
                            message: `Maximum age year must be at most ${MAX_AGE_YEARS}.`,
                          },
                          validate: (value, formValues) =>
                            value >= formValues.eventInfo.minAge.year ||
                            'Maximum age year must be greater than or equal to minimum age year.',
                          valueAsNumber: true,
                        })}
                        placeholder='year'
                      />
                      {typeof errors.eventInfo?.maxAge?.year?.message ===
                        'string' && (
                        <span className='text-sm text-destructive'>
                          {errors.eventInfo.maxAge.year.message}
                        </span>
                      )}
                    </div>
                    <div>
                      <Input
                        type='number'
                        {...register('eventInfo.maxAge.month', {
                          required: 'Enter the maximum age month.',
                          min: {
                            value: 0,
                            message: 'Maximum age month must be at least 0.',
                          },
                          max: {
                            value: 11,
                            message: 'Maximum age month must be at most 11.',
                          },

                          valueAsNumber: true,
                        })}
                        placeholder='month'
                      />
                      {typeof errors.eventInfo?.maxAge?.month?.message ===
                        'string' && (
                        <span className='text-sm text-destructive'>
                          {errors.eventInfo.maxAge.month.message}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className='space-y-2'>
                <Label>Instructor</Label>
                <Controller
                  control={control}
                  name='eventInfo.instructor'
                  render={({ field }) => (
                    <InstructorSelector
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                {errors.eventInfo?.instructor && (
                  <span className='text-sm text-destructive'>
                    {errors.eventInfo.instructor.message}
                  </span>
                )}
              </div>
              <div className='space-y-2'>
                <Label>Description</Label>
                <textarea
                  {...register('otherInfo.description')}
                  placeholder='Enter Event description'
                  className='h-24 w-full rounded-md border p-2'
                />
                {errors.otherInfo?.description && (
                  <span className='text-sm text-destructive'>
                    {errors.otherInfo.description.message}
                  </span>
                )}
              </div>
            </div>

            {/* Dates Section */}
            <div className='space-y-4'>
              <h2 className='text-xl font-semibold'>Dates</h2>
              <div className='flex flex-col space-y-4'>
                <div className='flex space-x-4'>
                  <div className='flex-1 space-y-2'>
                    <Label>Start Date</Label>
                    <Controller
                      control={control}
                      name='dates.startDate'
                      rules={{
                        required: 'Start date is required',
                        validate: (value, formValues) => {
                          if (!value) return true;

                          // Check if we have a session selected
                          const sessionValue =
                            formValues.eventInfo.session.value;
                          if (!sessionValue) return true;

                          // Check if session data is available
                          if (!sessionData) return true;

                          // Type guard to ensure sessionData is a valid session object
                          if (!('billingDate' in sessionData)) return true;

                          // Check if billing date exists
                          if (!sessionData.billingDate) return true;

                          // Compare dates using moment
                          const billingMoment = moment(
                            sessionData.billingDate,
                          ).startOf('day');
                          const classMoment = moment(value).startOf('day');

                          const formattedBillingDate =
                            billingMoment.format('MMM D, YYYY');

                          return (
                            billingMoment.isSameOrBefore(classMoment) ||
                            `Event start date must be on or after session billing date (${formattedBillingDate})`
                          );
                        },
                      }}
                      render={({ field }) => (
                        <DateTimePicker
                          hourCycle={24}
                          granularity='day'
                          displayFormat={{ hour24: 'PPP' }}
                          value={
                            field.value
                              ? new Date(
                                  field.value.includes('T')
                                    ? field.value
                                    : field.value + 'T00:00:00',
                                )
                              : undefined
                          }
                          onChange={(date) => {
                            field.onChange(
                              date ? format(date, 'yyyy-MM-dd') : undefined,
                            );
                          }}
                        />
                      )}
                    />
                    {errors.dates?.startDate && (
                      <span className='text-sm text-destructive'>
                        {errors.dates.startDate.message}
                      </span>
                    )}
                  </div>
                  <div className='flex-1 space-y-2'>
                    <Label>End Date</Label>
                    <Controller
                      control={control}
                      name='dates.endDate'
                      rules={{
                        required: 'End date is required',
                        validate: (value, formValues) => {
                          if (!value || !formValues.dates.startDate) return true;
                          return (
                            new Date(value.includes('T') ? value : value + 'T00:00:00') >=
                              new Date(
                                formValues.dates.startDate.includes('T')
                                  ? formValues.dates.startDate
                                  : formValues.dates.startDate + 'T00:00:00',
                              ) || 'End date must be on or after start date'
                          );
                        },
                      }}
                      render={({ field }) => (
                        <DateTimePicker
                          hourCycle={24}
                          granularity='day'
                          displayFormat={{ hour24: 'PPP' }}
                          value={
                            field.value
                              ? new Date(
                                  field.value.includes('T')
                                    ? field.value
                                    : field.value + 'T00:00:00',
                                )
                              : undefined
                          }
                          onChange={(date) => {
                            field.onChange(
                              date ? format(date, 'yyyy-MM-dd') : undefined,
                            );
                          }}
                        />
                      )}
                    />
                    {errors.dates?.endDate && (
                      <span className='text-sm text-destructive'>
                        {errors.dates.endDate.message}
                      </span>
                    )}
                  </div>
                </div>
                <div className='flex-1 space-y-2'>
                  <Label>Registration Start Date</Label>
                  <Controller
                    control={control}
                    name='dates.registrationStartDate'
                    rules={{
                      validate: (value, formValues) => {
                        if (!value) return true; // Not required
                        return (
                          new Date(
                            value.includes('T') ? value : value + 'T00:00:00',
                          ) <
                            new Date(
                              formValues.dates.startDate.includes('T')
                                ? formValues.dates.startDate
                                : formValues.dates.startDate + 'T00:00:00',
                            ) ||
                          'Registration start date must be before event start date'
                        );
                      },
                    }}
                    render={({ field }) => (
                      <DateTimePicker
                        hourCycle={24}
                        granularity='day'
                        displayFormat={{ hour24: 'PPP' }}
                        value={
                          field.value
                            ? new Date(
                                field.value.includes('T')
                                  ? field.value
                                  : field.value + 'T00:00:00',
                              )
                            : undefined
                        }
                        onChange={(date) => {
                          field.onChange(
                            date ? format(date, 'yyyy-MM-dd') : undefined,
                          );
                        }}
                      />
                    )}
                  />
                  {errors.dates?.registrationStartDate && (
                    <span className='text-sm text-destructive'>
                      {errors.dates.registrationStartDate.message}
                    </span>
                  )}
                  {watch('otherInfo.tuitionBillingCycle') ===
                    TuitionBillingCycle.ONE_TIME && (
                    <>
                      <div className='space-y-4'>
                        <h2 className='text-xl font-semibold'>Days</h2>
                        <div className='flex flex-wrap gap-4'>
                          {DAYS.map((day) => (
                            <div
                              key={day}
                              className='flex items-center space-x-2'
                            >
                              <Checkbox
                                id={day}
                                checked={selectedDays.includes(day)}
                                onCheckedChange={(checked) => {
                                  const currentDays = selectedDays;
                                  const updatedDays = checked
                                    ? [...currentDays, day]
                                    : currentDays.filter((d) => d !== day);

                                  // Set the value and force validation to run
                                  setValue('days', updatedDays, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                    shouldTouch: true,
                                  });

                                  // Force re-render to show validation message immediately
                                  trigger('days');
                                }}
                              />
                              <Label htmlFor={day}>{day}</Label>
                            </div>
                          ))}
                        </div>
                        {errors.days && (
                          <span className='mt-2 block text-sm font-medium text-destructive'>
                            {errors.days.message}
                          </span>
                        )}
                      </div>
                      <EventDateSelectionSwitch
                        checked={watch('eventInfo.allowSpecificDatesSelection')}
                        onCheckedChange={(checked) => {
                          setValue(
                            'eventInfo.allowSpecificDatesSelection',
                            checked,
                          );
                        }}
                      />
                      <div className='flex space-x-4'>
                        <div className='flex-1 space-y-2'>
                          <Label>Start Time</Label>
                          <Controller
                            control={control}
                            name='times.startTime'
                            rules={{ required: 'Start time is required' }}
                            render={({ field }) => (
                              <TimePickerDropdown
                                value={field.value}
                                onChange={(date) => {
                                  field.onChange(date);
                                }}
                              />
                            )}
                          />
                        </div>
                        <div className='flex-1 space-y-2'>
                          <Label>End Time</Label>
                          <Controller
                            control={control}
                            name='times.endTime'
                            rules={{
                              required: 'End time is required',
                              validate: (value) => {
                                const currentStartTime = watch('times.startTime');
                                if (!currentStartTime || !value) return true;
                                return (
                                  isValidTimeRange(
                                    normalizeTime(currentStartTime),
                                    normalizeTime(value),
                                  ) ||
                                  'End time must be after start time'
                                );
                              },
                            }}
                            render={({ field }) => (
                              <TimePickerDropdown
                                value={field.value}
                                onChange={(date) => {
                                  field.onChange(date);
                                }}
                              />
                            )}
                          />
                          {errors.times?.endTime && (
                            <span className='text-sm text-destructive'>
                              {errors.times.endTime.message}
                            </span>
                          )}
                        </div>
                        <div className='flex-1 space-y-2'>
                          <Label>Duration (minutes)</Label>
                          <Input
                            type='text'
                            {...register('times.duration')}
                            disabled
                            value={calculateDurationInMinutes(
                              startTime,
                              endTime,
                            )}
                            placeholder='MM'
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Times Section */}
            {watch('otherInfo.tuitionBillingCycle') !==
              TuitionBillingCycle.ONE_TIME && (
              <div className='space-y-4'>
                <h2 className='text-xl font-semibold'>Times</h2>

                <div className='space-y-2'>
                  <Label>Time Configuration</Label>
                  <Select
                    value={watch('timeConfig')}
                    onValueChange={(value) => {
                      setValue('timeConfig', value as 'same' | 'different');
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select time configuration' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='same'>
                        Same Time for All Selected Days
                      </SelectItem>
                      <SelectItem value='different'>
                        Different Times for Each Selected Day
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <EventDateSelectionSwitch
                  checked={watch('eventInfo.allowSpecificDatesSelection')}
                  onCheckedChange={(checked) => {
                    setValue('eventInfo.allowSpecificDatesSelection', checked);
                  }}
                />

                {watch('timeConfig') === 'same' ? (
                  <>
                    <div className='space-y-4'>
                      <h2 className='text-xl font-semibold'>Days</h2>
                      <div className='flex flex-wrap gap-4'>
                        {DAYS.map((day) => (
                          <div
                            key={day}
                            className='flex items-center space-x-2'
                          >
                            <Checkbox
                              id={day}
                              checked={selectedDays.includes(day)}
                              onCheckedChange={(checked) => {
                                const currentDays = selectedDays;
                                const updatedDays = checked
                                  ? [...currentDays, day]
                                  : currentDays.filter((d) => d !== day);

                                // Set the value and force validation to run
                                setValue('days', updatedDays, {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                  shouldTouch: true,
                                });

                                // Force re-render to show validation message immediately
                                trigger('days');
                              }}
                            />
                            <Label htmlFor={day}>{day}</Label>
                          </div>
                        ))}
                      </div>
                      {errors.days && (
                        <span className='mt-2 block text-sm font-medium text-destructive'>
                          {errors.days.message}
                        </span>
                      )}
                    </div>

                    <div className='flex space-x-4'>
                      <div className='flex-1 space-y-2'>
                        <Label>Start Time</Label>
                        <Controller
                          control={control}
                          name='times.startTime'
                          rules={{ required: 'Start time is required' }}
                          render={({ field }) => (
                            <TimePickerDropdown
                              value={field.value}
                              onChange={(date) => {
                                field.onChange(date);
                              }}
                            />
                          )}
                        />
                      </div>
                      <div className='flex-1 space-y-2'>
                        <Label>End Time</Label>
                        <Controller
                          control={control}
                          name='times.endTime'
                          rules={{
                            required: 'End time is required',
                            validate: (value) => {
                              const currentStartTime = watch('times.startTime');
                              if (!currentStartTime || !value) return true;
                              return (
                                isValidTimeRange(
                                  normalizeTime(currentStartTime),
                                  normalizeTime(value),
                                ) ||
                                'End time must be after start time'
                              );
                            },
                          }}
                          render={({ field }) => (
                            <TimePickerDropdown
                              value={field.value}
                              onChange={(date) => {
                                field.onChange(date);
                              }}
                            />
                          )}
                        />
                      </div>
                      <div className='flex-1 space-y-2'>
                        <Label>Duration (minutes)</Label>
                        <Input
                          type='text'
                          {...register('times.duration')}
                          disabled
                          value={calculateDurationInMinutes(startTime, endTime)}
                          placeholder='MM'
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className='space-y-6'>
                    <div className='flex items-center gap-4'>
                      <div className='w-20'></div>
                      <div className='flex flex-1 space-x-4'>
                        <div className='flex-1 text-center'>
                          <Label>Start Time</Label>
                        </div>
                        <div className='flex w-8 items-center'></div>
                        <div className='flex-1 text-center'>
                          <Label>End Time</Label>
                        </div>
                      </div>
                    </div>

                    {DAYS.map((day, index) => (
                      <div key={day}>
                        <div className='flex h-5 items-center gap-4'>
                          <Checkbox
                            id={day}
                            checked={selectedDays.includes(day)}
                            onCheckedChange={(checked) => {
                              const currentDays = selectedDays;
                              const updatedDays = checked
                                ? [...currentDays, day]
                                : currentDays.filter((d) => d !== day);

                              // Set the value and force validation to run
                              setValue('days', updatedDays, {
                                shouldValidate: true,
                                shouldDirty: true,
                                shouldTouch: true,
                              });

                              // Force re-render to show validation message immediately
                              trigger('days');
                            }}
                          />
                          <Label htmlFor={day} className='w-20'>
                            {day}
                          </Label>

                          <div className='flex flex-1 space-x-4'>
                            {selectedDays.includes(day) ? (
                              <>
                                <div className='flex-1'>
                                  <Controller
                                    control={control}
                                    name={`dayTimes.${day}.startTime`}
                                    rules={{
                                      required: 'Start time is required',
                                    }}
                                    render={({ field }) => (
                                      <TimePickerDropdown
                                        value={field.value}
                                        onChange={(date) => {
                                          field.onChange(date);
                                        }}
                                      />
                                    )}
                                  />
                                </div>
                                <div className='flex w-8 items-center justify-center'></div>
                                <div className='flex-1'>
                                  <Controller
                                    control={control}
                                    name={`dayTimes.${day}.endTime`}
                                    rules={{
                                      required: 'End time is required',
                                      validate: (value) => {
                                        const dayStartTime = watch(
                                          `dayTimes.${day}.startTime`,
                                        );
                                        if (!dayStartTime || !value) return true;
                                        return (
                                          isValidTimeRange(
                                            normalizeTime(dayStartTime),
                                            normalizeTime(value),
                                          ) ||
                                          'End time must be after start time'
                                        );
                                      },
                                    }}
                                    render={({ field }) => (
                                      <TimePickerDropdown
                                        value={field.value}
                                        onChange={(date) => {
                                          field.onChange(date);
                                        }}
                                      />
                                    )}
                                  />
                                </div>
                              </>
                            ) : (
                              <div className='flex-1 italic text-gray-500'></div>
                            )}
                          </div>
                        </div>
                        {index < DAYS.length - 1 && (
                          <div className='my-4 h-px w-full bg-border' />
                        )}
                      </div>
                    ))}
                    {errors.days && (
                      <span className='mt-2 block text-sm font-medium text-destructive'>
                        {errors.days.message}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className='space-y-4'>
              <h2 className='text-xl font-semibold'>Tuition Info</h2>
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <div className='space-y-2'>
                  <Label>Tuition Fee</Label>
                  <Input
                    {...register('otherInfo.tuitionFee', {
                      min: {
                        value: 0,
                        message: 'Tuition fee cannot be negative',
                      },
                      valueAsNumber: true,
                    })}
                    type='number'
                    min={0}
                    step='0.01'
                    placeholder='0.00'
                  />
                  {errors.otherInfo?.tuitionFee && (
                    <span className='text-sm text-destructive'>
                      {errors.otherInfo.tuitionFee.message}
                    </span>
                  )}
                </div>

                <div className='space-y-2'>
                  <Label>Event Registration Fee</Label>
                  <Input
                    {...register('otherInfo.registrationFee', {
                      min: {
                        value: 0,
                        message: 'Registration fee cannot be negative',
                      },
                      valueAsNumber: true,
                    })}
                    type='number'
                    min={0}
                    step='1'
                    placeholder='0.00'
                  />
                  {errors.otherInfo?.registrationFee && (
                    <span className='text-sm text-destructive'>
                      {errors.otherInfo.registrationFee.message}
                    </span>
                  )}
                </div>

                <div className='space-y-2'>
                  <Label>Costume Fee</Label>
                  <Input
                    {...register('otherInfo.costumeFee', {
                      min: { value: 0, message: 'Fee cannot be negative' },
                      pattern: {
                        value: /^\d+(\.\d{1,2})?$/,
                        message: 'Please enter a valid amount',
                      },
                    })}
                    type='number'
                    min={0}
                    step='1'
                    placeholder='0.00'
                  />
                  {errors.otherInfo?.costumeFee && (
                    <span className='text-sm text-destructive'>
                      {errors.otherInfo.costumeFee.message}
                    </span>
                  )}
                </div>

                <div className='space-y-2'>
                  <Label>Tuition Billing Cycle</Label>
                  <Controller
                    control={control}
                    name='otherInfo.tuitionBillingCycle'
                    rules={{ required: 'Tuition billing cycle is required' }}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select Billing Cycle' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={TuitionBillingCycle.ONE_TIME}>
                            One Time
                          </SelectItem>
                          <SelectItem value='monthly'>Monthly</SelectItem>
                          <SelectItem value='weekly'>Weekly</SelectItem>
                          <SelectItem value='bi-weekly'>Bi Weekly</SelectItem>
                          <SelectItem value={TuitionBillingCycle.EVERY_X_DAYS}>
                            Every X Days
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.otherInfo?.tuitionBillingCycle && (
                    <span className='text-sm text-destructive'>
                      {errors.otherInfo.tuitionBillingCycle.message}
                    </span>
                  )}
                </div>

                {watch('otherInfo.tuitionBillingCycle') === TuitionBillingCycle.EVERY_X_DAYS && (
                  <div className='space-y-2'>
                    <Label>Billing Frequency (Days)</Label>
                    <Input
                      {...register('otherInfo.billingFrequencyDays', {
                        required:
                          'Billing frequency is required for every X days cycle',
                        min: {
                          value: 1,
                          message: 'Billing frequency must be at least 1 day',
                        },
                        valueAsNumber: true,
                      })}
                      type='number'
                      min={1}
                      placeholder='Enter number of days (e.g. 10)'
                      disabled
                    />
                    {errors.otherInfo?.billingFrequencyDays && (
                      <span className='text-sm text-destructive'>
                        {errors.otherInfo.billingFrequencyDays.message}
                      </span>
                    )}
                  </div>
                )}

                {watch('otherInfo.tuitionBillingCycle') === 'monthly' && (
                  <div className='space-y-2'>
                    <Label>Billing Day</Label>
                    <Controller
                      control={control}
                      name='otherInfo.billingDay'
                      rules={{
                        required: 'Billing day is required',
                        min: {
                          value: 1,
                          message: 'Billing day must be at least 1',
                        },
                        max: {
                          value: 28,
                          message: 'Billing day must be at most 28',
                        },
                      }}
                      render={({ field }) => (
                        <Input
                          type='number'
                          min={1}
                          max={28}
                          placeholder='Enter billing day (1-28)'
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => {
                            const val =
                              e.target.value === ''
                                ? ''
                                : parseInt(e.target.value, 10);
                            field.onChange(val);
                          }}
                        />
                      )}
                    />
                    {errors.otherInfo?.billingDay && (
                      <span className='text-sm text-destructive'>
                        {errors.otherInfo.billingDay.message}
                      </span>
                    )}
                  </div>
                )}

                {watch('otherInfo.tuitionBillingCycle') &&
                  watch('otherInfo.tuitionBillingCycle') !== 'hourly' &&
                  watch('otherInfo.tuitionBillingCycle') !== TuitionBillingCycle.ONE_TIME && (
                    <div className='flex items-center gap-2 col-span-2'>
                      <Switch
                        id='prorateTuition'
                        checked={studioProrationEnabled && watch('otherInfo.prorateTuition')}
                        onCheckedChange={(checked) => {
                          setValue('otherInfo.prorateTuition', checked);
                        }}
                        disabled={!studioProrationEnabled}
                      />
                      <Label htmlFor='prorateTuition' className={!studioProrationEnabled ? 'text-muted-foreground' : ''}>
                        Enable Proration
                      </Label>
                      {!studioProrationEnabled && (
                        <span className='text-xs text-muted-foreground'>
                          (Proration is disabled at studio level)
                        </span>
                      )}
                    </div>
                  )}
              </div>
            </div>

            {/* Other Info Section */}
            <div className='space-y-4'>
              <h2 className='text-xl font-semibold'>Other Info</h2>
              <div className='space-y-2'>
                <Label>Tags</Label>
                <Controller
                  control={control}
                  name='otherInfo.tags'
                  render={({ field }) => (
                    <TagsSelector
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <div className='space-y-2'>
                  <Label>Group</Label>
                  <Controller
                    control={control}
                    name='otherInfo.group'
                    render={({ field }) => (
                      <GroupsSelector
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
                <div className='space-y-2'>
                  <Label>Policy</Label>
                  <Controller
                    control={control}
                    name='otherInfo.policyGroup'
                    render={({ field }) => (
                      <PolicySelector
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  {errors.otherInfo?.policyGroup && (
                    <span className='text-sm text-destructive'>
                      {errors.otherInfo.policyGroup.message}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <Button type='submit' className='w-full'>
              {mutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Updating Event
                </>
              ) : (
                'Update Event'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {unsavedChangesDialog}
    </div>
  );
};
